import { CheckoutContext } from '@/context/CheckoutContext';
import { FontAwesomeIcon } from '@/components/Common/FontAwesomeIcon/FontAwesomeIcon';
import { useContext, useMemo } from 'react';
import { shouldHideXButton } from '@/utils/conditionalRendering';
import { useTranslate } from '@/hooks/useTranslate';
import { mixpanelService } from '@/services/mixpanelService';
import { MixpanelEvents } from '@/constants/mixpanelEvents';
import { getMixpanelProperties } from '@/utils/mixpanelProperties';
import { hasPublisherHaveBackgroundImage } from '@/utils/publisher';
import { cn } from '@/utils/cn';
import { useCancelOrder } from '@/api/order';

export const XButton = ({ onClick, style }: { onClick: () => void; style?: React.CSSProperties }) => {
  const context = useContext(CheckoutContext);
  const {
    payment: { isInPaymentProcess },
    featureFlags,
    successScreen,
  } = context;
  const hasBackgroundImage = hasPublisherHaveBackgroundImage(context.boot?.checkoutTheme, context.template.type);
  const { t } = useTranslate();
  const { mutateAsync: cancelOrder } = useCancelOrder();
  const showButton = useMemo(() => {
    return !shouldHideXButton(featureFlags);
  }, [featureFlags]);

  const handleClick = async () => {
    const properties = getMixpanelProperties(context);
    mixpanelService.track(MixpanelEvents.CHECKOUT_CLOSED, properties as Record<string, unknown>);
    if (!isInPaymentProcess || successScreen?.isOpen) {
      await cancelOrder();
    }
    onClick();
  };

  return showButton ? (
    <div
      className={cn(
        'relative h-5 md:mt-0 mt-0 w-full md:h-0 landscape:h-0 landscape:mt-0',
        isInPaymentProcess && 'hidden'
      )}
      style={style}
    >
      <button
        data-testid="close-checkout-button"
        onClick={handleClick}
        className={cn(
          'absolute top-2 right-2 md:top-6 md:right-6 w-6 h-6 flex items-center justify-center cursor-pointer rounded-full bg-white transition-all duration-200 z-50 pr-2',
          hasBackgroundImage && 'bg-transparent'
        )}
        aria-label={t('closeCheckout')}
      >
        <FontAwesomeIcon icon="fa-solid fa-xmark" />
      </button>
    </div>
  ) : null;
};
